import React, { useState, useEffect, useContext } from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  CircularProgress,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Alert from "@material-ui/lab/Alert";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";

const emptyForm = {
  title: "",
  value: "",
  valueDisplay: "",
  userId: "",
  expectedCloseDate: "",
  notes: "",
};

// converte "1.234,56" → 1234.56 (mesmo padrão do TransactionModal)
const parseBRL = (str) => {
  const clean = str.replace(/\./g, "").replace(",", ".");
  const n = parseFloat(clean);
  return isNaN(n) ? "" : n;
};

const formatBRL = (digits) => {
  const n = parseInt(digits.replace(/\D/g, ""), 10) || 0;
  return (n / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const OpportunityModal = ({ open, onClose, opportunity, initialContact }) => {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.profile === "admin";

  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // negócios já abertos do contato escolhido — evita duplicata acidental
  // quando a automação de entrada já criou um card para ele
  const [openOpportunities, setOpenOpportunities] = useState([]);

  // seleção de contato (só quando não há contato fixo nem edição)
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactOptions, setContactOptions] = useState([]);
  const [contactSearch, setContactSearch] = useState("");
  const [searching, setSearching] = useState(false);

  const fixedContact = opportunity?.contact || initialContact || null;
  const contactId = fixedContact?.id || selectedContact?.id || null;

  useEffect(() => {
    if (!open) return;

    const loadUsers = async () => {
      try {
        const { data } = await api.get("/users/");
        setUsers(data.users || []);
      } catch (err) {
        toastError(err);
      }
    };
    loadUsers();

    if (opportunity) {
      const rawValue = opportunity.value || 0;
      const digits = Math.round(Number(rawValue) * 100).toString();
      setForm({
        title: opportunity.title || "",
        value: rawValue || "",
        valueDisplay: rawValue ? formatBRL(digits) : "",
        userId: opportunity.userId || "",
        expectedCloseDate: opportunity.expectedCloseDate || "",
        notes: opportunity.notes || "",
      });
    } else {
      setForm(emptyForm);
    }
    setSelectedContact(null);
    setContactSearch("");
    setConfirmDelete(false);
  }, [open, opportunity]);

  // aviso de duplicata: só na criação, quando já existe negócio aberto
  useEffect(() => {
    if (!open || opportunity || !contactId) {
      setOpenOpportunities([]);
      return;
    }
    const loadOpen = async () => {
      try {
        const { data } = await api.get("/opportunities", {
          params: { contactId, status: "open" },
        });
        setOpenOpportunities(data.opportunities || []);
      } catch (err) {
        // o aviso é conveniência — falha aqui não pode bloquear a criação
      }
    };
    loadOpen();
  }, [open, opportunity, contactId]);

  useEffect(() => {
    if (!open || fixedContact || contactSearch.length < 2) return;
    setSearching(true);
    const delayDebounceFn = setTimeout(async () => {
      try {
        const { data } = await api.get("contacts", {
          params: { searchParam: contactSearch },
        });
        setContactOptions(data.contacts || []);
      } catch (err) {
        toastError(err);
      } finally {
        setSearching(false);
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [contactSearch, open, fixedContact]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleValueChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    if (!digits) {
      setForm((prev) => ({ ...prev, value: "", valueDisplay: "" }));
      return;
    }
    const display = formatBRL(digits);
    setForm((prev) => ({ ...prev, value: parseBRL(display), valueDisplay: display }));
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await api.delete(`/opportunities/${opportunity.id}`);
      toast.success("Negócio excluído.");
      onClose(true);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error("Informe o título do negócio.");
      return;
    }
    if (!opportunity && !contactId) {
      toast.error("Selecione o contato do negócio.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        title: form.title,
        value: form.value === "" ? null : form.value,
        userId: form.userId === "" ? null : form.userId,
        expectedCloseDate: form.expectedCloseDate || null,
        notes: form.notes || null,
      };

      let saved;
      if (opportunity) {
        const { data } = await api.put(`/opportunities/${opportunity.id}`, payload);
        saved = data;
        toast.success("Negócio atualizado.");
      } else {
        const { data } = await api.post("/opportunities", {
          ...payload,
          contactId,
        });
        saved = data;
        toast.success("Negócio criado.");
      }
      onClose(saved);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ConfirmationModal
        title="Excluir negócio"
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
      >
        O negócio "{opportunity?.title}" e todo o histórico de movimentação dele
        serão apagados, inclusive dos relatórios do funil. As conversas
        vinculadas não são afetadas.
      </ConfirmationModal>

      <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
        <DialogTitle>
          {opportunity ? "Editar negócio" : "Novo negócio"}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {fixedContact ? (
                <TextField
                  label="Contato"
                  value={fixedContact.name || ""}
                  fullWidth
                  variant="outlined"
                  size="small"
                  disabled
                />
              ) : (
                <Autocomplete
                  options={contactOptions}
                  loading={searching}
                  value={selectedContact}
                  getOptionLabel={(option) => option.name || ""}
                  onChange={(e, newValue) => setSelectedContact(newValue)}
                  onInputChange={(e, newInputValue) => setContactSearch(newInputValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Contato"
                      variant="outlined"
                      size="small"
                      autoFocus
                      placeholder="Digite para buscar..."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {searching ? <CircularProgress color="inherit" size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            </Grid>

            {openOpportunities.length > 0 && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  Este contato já tem{" "}
                  {openOpportunities.length === 1
                    ? "um negócio aberto"
                    : `${openOpportunities.length} negócios abertos`}
                  :{" "}
                  {openOpportunities
                    .map((o) =>
                      o.stage?.name ? `${o.title} (${o.stage.name})` : o.title
                    )
                    .join(", ")}
                  . Criar outro gera um card separado no funil.
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                label="Título"
                value={form.title}
                onChange={handleChange("title")}
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Ex: Pacote trimestral"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Valor (R$)"
                value={form.valueDisplay}
                onChange={handleValueChange}
                fullWidth
                variant="outlined"
                size="small"
                placeholder="0,00"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Previsão de fechamento"
                type="date"
                value={form.expectedCloseDate}
                onChange={handleChange("expectedCloseDate")}
                fullWidth
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Responsável</InputLabel>
                <Select
                  value={form.userId}
                  onChange={handleChange("userId")}
                  label="Responsável"
                >
                  <MenuItem value="">
                    <em>Sem responsável</em>
                  </MenuItem>
                  {users.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Observações"
                value={form.notes}
                onChange={handleChange("notes")}
                fullWidth
                variant="outlined"
                size="small"
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          {opportunity && isAdmin && (
            <Button
              onClick={() => setConfirmDelete(true)}
              disabled={loading}
              color="secondary"
              startIcon={<DeleteOutlineIcon />}
              style={{ marginRight: "auto" }}
            >
              Excluir
            </Button>
          )}
          <Button onClick={() => onClose()} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OpportunityModal;
